import { useEffect, useState } from 'react';

const stats = [
  { value: '500+', label: 'SA Businesses' },
  { value: '40%', label: 'Efficiency Gain' },
  { value: '65%', label: 'Fewer Stockouts' },
  { value: '24/7', label: 'Support' },
];

const Hero = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#0a1628]" id="hero">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/b9d121c4d6eebb340f29b3d410564881.jpg"
          alt="Automotive Workshop"
          className="w-full h-full object-cover object-top opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628] via-[#0a1628]/80 to-[#0a1628]/60"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0a1628]"></div>
      </div>

      {/* Glowing orbs */}
      <div className="absolute top-32 right-1/4 w-96 h-96 bg-[#1e40af]/20 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-32 left-1/3 w-64 h-64 bg-[#f97316]/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div
            className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#f97316]/15 border border-[#f97316]/30 rounded-full mb-6">
              <span className="w-2 h-2 bg-[#f97316] rounded-full animate-pulse"></span>
              <span className="text-[#f97316] text-xs font-semibold tracking-wide uppercase">
                Built for South Africa
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Workshop
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#3b82f6] to-[#f97316]">
                Software Built for
              </span>
              South Africa
            </h1>

            <p className="text-gray-300 text-lg leading-relaxed mb-8 max-w-xl">
              Run your workshop smarter — manage service bays, track jobs, assign technicians, and handle invoicing all from one powerful platform. Built for South African auto shops and service centres.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <a
                href="#demo"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#f97316] hover:bg-[#ea6c0a] text-white font-semibold rounded-lg transition-all duration-200 text-sm whitespace-nowrap"
              >
                <i className="ri-calendar-check-line"></i>
                Book Automotive Demo
              </a>
              <a
                href="#pricing"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold rounded-lg transition-all duration-200 text-sm whitespace-nowrap"
              >
                <i className="ri-price-tag-3-line"></i>
                View Pricing
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {stats.map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-2xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {s.value}
                  </div>
                  <div className="text-gray-400 text-xs mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Workshop Dashboard mockup */}
          <div
            className={`transition-all duration-1000 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <div className="relative">
              {/* Main dashboard card */}
              <div className="bg-[#0f2040] border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
                {/* Dashboard header */}
                <div className="bg-[#0d1b36] px-5 py-3 flex items-center justify-between border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-red-500/70"></span>
                      <span className="w-3 h-3 rounded-full bg-yellow-500/70"></span>
                      <span className="w-3 h-3 rounded-full bg-green-500/70"></span>
                    </div>
                    <span className="text-gray-400 text-xs">PRODIFY — Workshop Manager</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <i className="ri-shield-check-line text-green-400"></i>
                    <span>Live</span>
                  </div>
                </div>

                {/* Dashboard body */}
                <div className="p-5">

                  {/* Workshop KPI row */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {[
                      { label: 'Active Jobs', value: '14', icon: 'ri-tools-line', color: 'text-[#f97316]', bg: 'bg-[#f97316]/10' },
                      { label: 'Bays In Use', value: '6 / 8', icon: 'ri-car-line', color: 'text-[#3b82f6]', bg: 'bg-[#3b82f6]/10' },
                      { label: "Today's Revenue", value: 'R31,200', icon: 'ri-hand-coin-line', color: 'text-green-400', bg: 'bg-green-400/10' },
                    ].map((kpi) => (
                      <div key={kpi.label} className={`${kpi.bg} rounded-xl p-3`}>
                        <div className="w-6 h-6 flex items-center justify-center mb-2">
                          <i className={`${kpi.icon} ${kpi.color} text-lg`}></i>
                        </div>
                        <div className="text-white font-bold text-sm">{kpi.value}</div>
                        <div className="text-gray-400 text-xs mt-0.5">{kpi.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Active jobs list */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white text-xs font-semibold">Active Service Jobs</span>
                      <span className="text-[#3b82f6] text-xs cursor-pointer">View All</span>
                    </div>
                    <div className="space-y-2">
                      {[
                        { vehicle: 'Toyota Hilux — GP 45 ABC', job: 'Full Service + Brake Pads', tech: 'Sipho M.', status: 'In Progress', progress: 70 },
                        { vehicle: 'VW Polo — CL 12 XYZ', job: 'Engine Diagnostics', tech: 'Ruan V.', status: 'Awaiting Parts', progress: 30 },
                        { vehicle: 'BMW 3 Series — CA 99 KLM', job: 'Gearbox Service', tech: 'Thabo N.', status: 'Ready', progress: 100 },
                      ].map((job) => (
                        <div key={job.vehicle} className="bg-[#0a1628] rounded-lg px-3 py-2">
                          <div className="flex items-center justify-between mb-1.5">
                            <div>
                              <div className="text-white text-xs font-medium">{job.vehicle}</div>
                              <div className="text-gray-400 text-xs">{job.job} · <span className="text-gray-500">{job.tech}</span></div>
                            </div>
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                                job.status === 'Ready'
                                  ? 'bg-green-400/10 text-green-400'
                                  : job.status === 'In Progress'
                                  ? 'bg-[#f97316]/10 text-[#f97316]'
                                  : 'bg-yellow-400/10 text-yellow-400'
                              }`}
                            >
                              {job.status}
                            </span>
                          </div>
                          {/* Progress bar */}
                          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${job.progress === 100 ? 'bg-green-400' : job.progress >= 60 ? 'bg-[#f97316]' : 'bg-yellow-400'}`}
                              style={{ width: `${job.progress}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Technician availability + parts snippet */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Technicians */}
                    <div className="bg-[#0a1628] rounded-xl p-3">
                      <div className="text-white text-xs font-semibold mb-2">Technicians</div>
                      <div className="space-y-1.5">
                        {[
                          { name: 'Sipho M.', status: 'Busy', dot: 'bg-[#f97316]' },
                          { name: 'Ruan V.', status: 'Busy', dot: 'bg-[#f97316]' },
                          { name: 'Thabo N.', status: 'Free', dot: 'bg-green-400' },
                          { name: 'Keegan L.', status: 'Free', dot: 'bg-green-400' },
                        ].map((t) => (
                          <div key={t.name} className="flex items-center justify-between">
                            <span className="text-gray-300 text-xs">{t.name}</span>
                            <div className="flex items-center gap-1.5">
                              <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`}></span>
                              <span className="text-gray-400 text-xs">{t.status}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Parts — secondary, smaller */}
                    <div className="bg-[#0a1628] rounded-xl p-3">
                      <div className="text-white text-xs font-semibold mb-2">Parts Needed</div>
                      <div className="space-y-1.5">
                        {[
                          { part: 'Brake Pads Hilux', status: 'In Stock', color: 'text-green-400' },
                          { part: 'Oil Filter Polo', status: 'Low', color: 'text-yellow-400' },
                          { part: 'Alt. BMW 3 Series', status: 'Order', color: 'text-red-400' },
                        ].map((p) => (
                          <div key={p.part} className="flex items-center justify-between">
                            <span className="text-gray-300 text-xs truncate pr-1">{p.part}</span>
                            <span className={`text-xs font-medium whitespace-nowrap ${p.color}`}>{p.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Floating notification card */}
              <div className="absolute -bottom-4 -left-4 bg-[#0f2040] border border-[#f97316]/30 rounded-xl p-3 shadow-xl hidden sm:block">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#f97316]/15 rounded-lg">
                    <i className="ri-check-double-line text-[#f97316] text-sm"></i>
                  </div>
                  <div>
                    <div className="text-white text-xs font-semibold">Job Complete</div>
                    <div className="text-gray-400 text-xs">BMW 3 Series — Ready for collection</div>
                  </div>
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -top-4 -right-4 bg-[#3b82f6] rounded-xl p-3 shadow-xl hidden sm:block">
                <div className="text-white text-xs font-bold">VAT Ready</div>
                <div className="text-blue-200 text-xs">SA Compliant</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0a1628] to-transparent pointer-events-none z-10"></div>
    </section>
  );
};

export default Hero;
